/**
 * Compact feedback composer, anchored at the Alt+right-click point.
 *
 * Reads the live viewport snapshot (active slot's series/slice/zoom) so model-
 * behavior feedback carries where the reporter was looking, assembles the
 * reproduction context, optionally grabs a screenshot, and POSTs the note.
 */

import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { activeSlotAtom, slotStateFamily } from "@/atoms/hangingProtocolAtoms";

import { submitFeedback } from "./feedbackApi";
import {
  buildContext,
  FEEDBACK_CATEGORIES,
  type CapturedTarget,
  type FeedbackCategory,
  type ViewportSnapshot,
} from "./feedbackCapture";
import { captureScreenshot } from "./feedbackScreenshot";

const WIDTH = 340;
const EST_HEIGHT = 300;
const MARGIN = 8;

type Status = "editing" | "submitting" | "sent" | "error";

interface Props {
  anchor: { x: number; y: number };
  target?: CapturedTarget;
  onClose: () => void;
}

/** Live viewer state, or undefined when no series is loaded (not on the viewer).
 *  A reformat (MPR) slot's own `seriesId` is null (see SlotState.reslice) — it
 *  renders another series' volume resliced to a different plane — so this
 *  falls back to `reslice.sourceSeriesId`. Without it, filing feedback while
 *  looking at a reformat pane would silently drop the whole reproduction
 *  context (series/slice/zoom) this composer exists to capture. Note the
 *  captured `sliceIndex` is still in THIS pane's own reslice index space, not
 *  the source series' native index space — it identifies what the reporter
 *  was looking at, not a slice you can look up directly in the source series,
 *  so don't reuse this snapshot to index into the source series elsewhere. */
function useViewportSnapshot(): ViewportSnapshot | undefined {
  const slot = useAtomValue(activeSlotAtom);
  const state = useAtomValue(slotStateFamily(slot));
  const seriesId = state.reslice?.sourceSeriesId ?? state.seriesId;
  if (!seriesId) return undefined;
  return {
    slot,
    seriesId,
    sliceIndex: state.sliceIndex,
    totalSlices: state.totalSlices,
    zoom: state.zoom,
  };
}

export function FeedbackComposer({ anchor, target, onClose }: Props) {
  const viewport = useViewportSnapshot();
  const [category, setCategory] = useState<FeedbackCategory>("ui_wording");
  const [text, setText] = useState("");
  const [withScreenshot, setWithScreenshot] = useState(true);
  const [status, setStatus] = useState<Status>("editing");
  const [fbId, setFbId] = useState<string | null>(null);
  const [screenshotDropped, setScreenshotDropped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    return () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    };
  }, []);

  const canSubmit = text.trim().length > 0 && (status === "editing" || status === "error");

  const handleSubmit = useCallback(async () => {
    if (text.trim().length === 0 || status === "submitting") return;
    setStatus("submitting");
    setError(null);
    try {
      const screenshot = withScreenshot ? await captureScreenshot() : null;
      const context = buildContext({ target, viewport });
      const res = await submitFeedback({
        category,
        text: text.trim(),
        context,
        screenshot,
      });
      setFbId(res.fbId);
      // The note landed; the image may not have (capture or upload failed).
      const dropped = withScreenshot && !res.screenshotUploaded;
      setScreenshotDropped(dropped);
      setStatus("sent");
      // Linger longer when there is a warning to read.
      closeTimer.current = window.setTimeout(onClose, dropped ? 2600 : 1400);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send feedback");
      setStatus("error");
    }
  }, [category, text, withScreenshot, target, viewport, status, onClose]);

  // Closing mid-submit unmounts the composer while the POST is in flight — the
  // note still lands server-side, but the reporter never sees the FB id or the
  // screenshot warning. Backdrop clicks and Escape both wait the submit out.
  const closeUnlessSubmitting = () => {
    if (status !== "submitting") onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      closeUnlessSubmitting();
    } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const left = Math.max(MARGIN, Math.min(anchor.x, window.innerWidth - WIDTH - MARGIN));
  const top = Math.max(MARGIN, Math.min(anchor.y, window.innerHeight - EST_HEIGHT - MARGIN));
  const hint = target?.text || window.location.pathname;

  return createPortal(
    <>
      <div
        data-html2canvas-ignore="true"
        className="fixed inset-0 z-1000"
        onMouseDown={closeUnlessSubmitting}
      />
      <div
        data-html2canvas-ignore="true"
        role="dialog"
        aria-label="Send feedback"
        onKeyDown={onKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ left, top, width: WIDTH }}
        className="fixed z-1001 rounded-lg border border-border-strong bg-toast-bg p-3 text-fg shadow-xl shadow-black/40"
      >
        {status === "sent" ? (
          <div className="py-2 text-center text-sm">
            <div className={screenshotDropped ? "text-warn" : "text-fg"}>
              {screenshotDropped ? "Sent — screenshot didn't attach" : "Thanks — feedback sent"}
            </div>
            <div className="mt-1 font-mono text-xs text-signal-text-tint">{fbId}</div>
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <span className="text-xs font-medium text-fg">Send feedback</span>
              <span className="truncate text-[11px] text-fg-dim" title={hint}>
                {hint}
              </span>
            </div>

            <div className="mb-2 flex flex-wrap gap-1">
              {FEEDBACK_CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategory(c.value)}
                  className={
                    "rounded border px-2 py-0.5 text-[11px] transition-colors " +
                    (category === c.value
                      ? "border-signal-stroke bg-signal-tint text-signal-text-tint"
                      : "border-border bg-surface-2 text-fg-muted hover:text-fg")
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>

            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="What's off? (button wording, model overcalling X, a workflow snag…)"
              className="w-full resize-none rounded border border-border bg-surface px-2 py-1.5 text-sm text-fg placeholder:text-fg-faint focus:border-signal-stroke focus:outline-none"
            />

            <label className="mt-2 flex cursor-pointer items-center gap-1.5 text-[11px] text-fg-muted">
              <input
                type="checkbox"
                checked={withScreenshot}
                onChange={(e) => setWithScreenshot(e.target.checked)}
              />
              Attach screenshot
            </label>

            {error && <div className="mt-2 text-[11px] text-warn">{error}</div>}

            <div className="mt-3 flex items-center justify-between">
              <span className="text-[11px] text-fg-faint">⌘↵ send · esc cancel</span>
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={!canSubmit}
                className="rounded bg-signal px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
              >
                {status === "submitting" ? "Sending…" : status === "error" ? "Retry" : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </>,
    document.body,
  );
}
